import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { Box, useMediaQuery, useTheme } from '@mui/material'

import {
  AccountIconEditable,
  AccountTabLinks,
  AccountUsers,
  FormTimestamp,
  FormTitle,
  AccountSettings,
  Loader,
} from 'src/components'
import { AccountBilling } from '../account-billing'
import { MobileIcon } from './mobile-icon.component'
import { EditAccountForm } from './edit-account-form'
import { useMenuItems, useAccount, useUser } from 'src/hooks'
import { EAccountRoles, EUserRoles } from 'src/types'
import { DEFAULT_LANGUAGE_CODE } from 'src/components/languages/translations-list/constants'
import { ScanExplorerButton } from '../common/account-profile-form/scan-explorer'
import { ColoredChip } from 'src/components/colored-chip'

const EditAccount = () => {
  const [searchParams] = useSearchParams()
  const [t] = useTranslation()
  const [locale, setLocale] = useState(DEFAULT_LANGUAGE_CODE)
  const [enableReinitialize, setEnableReinitialize] = useState(false)
  const { data: user } = useUser()
  const { data: account, isSuccess, isLoading } = useAccount(locale)
  const theme = useTheme()

  useEffect(() => {
    if (account && !isLoading) {
      setEnableReinitialize(false)
    }
  }, [account, isLoading])

  const hideBilling = isSuccess && account?.white_label?.billing_hidden
  let items = [
    { title: 'account-add-header-title.v', isActive: true, tab: 'profile' },
    { title: 'edit-account-title-1.v', isActive: false, tab: 'billing' },
    { title: 'account-settings-header-title.v', isActive: false, tab: 'settings' },
  ]

  // Conditionally modify the items array based on the hideBilling flag
  if (hideBilling) {
    items = items.filter((item) => item.tab !== 'billing')
  }

  const { handleChooseMenuItem, menuItems, setMenuItems } = useMenuItems(items)

  const isAdmin = [EAccountRoles.admin, EAccountRoles.owner].some((role) =>
    account?.currentRoles.includes(role),
  )
  const isSuperadmin = user?.roles.includes(EUserRoles.superadmin)
  const isAccountAdmin = isAdmin || isSuperadmin

  useEffect(() => {
    if (isAccountAdmin) {
      setMenuItems((currentItems) => {
        const usersItem = {
          title: t('account-users-header-title.v'),
          isActive: false,
          tab: 'users',
        }
        if (!currentItems.some((item) => item.tab === 'users')) {
          return [...currentItems.slice(0, 2), usersItem, ...currentItems.slice(2)]
        }
        return currentItems
      })
    }
  }, [t, setMenuItems, isAccountAdmin])

  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))

  const handleLocaleChange = (locale: string) => {
    setEnableReinitialize(true)

    setLocale(() => locale)
  }

  const curTab = searchParams.get('tab')

  if (isLoading || !account || !user) {
    return <Loader />
  }

  return (
    <>
      {!isTablet ? (
        <FormTitle
          statusType="account"
          productStatus={account?.public}
          showStatus
          menuItems={menuItems}
          handleChooseMenuItem={handleChooseMenuItem}
          title={account.account_holder ?? ''}
          imageComponent={<AccountIconEditable />}
          afterTitleNode={
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
              {/* <LocaleSelect locale={locale} handleLocale={handleLocaleChange} /> */}
              {account && !account?.ai_generated ? (
                <ScanExplorerButton
                  locale={locale}
                  tokenId={account?.token_id}
                  isMinting={account?.is_minting}
                  arweaveUri={account?.arweave_url}
                  isAccountOwner={!!isAccountAdmin}
                  notShowNftButton
                />
              ) : null}
            </div>
          }
        />
      ) : (
        <MobileIcon
          locale={locale}
          tokenId={account.token_id}
          isAccountOwner={isAdmin || account.currentRoles.includes(EAccountRoles.owner)}
          arweaveUri={account.arweave_url}
          isMinting={account.is_minting}
          aiGenerated={account?.ai_generated}
          additionalItem={
            <Box sx={{ marginLeft: 'auto' }}>
              <ColoredChip
                color={account?.public ? 'green' : '#E57373'}
                text={
                  account?.public ? t('product-public-lable.v') : t('product-not-public-lable.v')
                }
                // icon={
                //   productStatus ? null : (
                //     <IconButton
                //       sx={{
                //         padding: '2px',
                //         cursor: 'pointer',
                //       }}
                //       onClick={() => {
                //         handlOpen(
                //           createProductQRLink({
                //             productId: simpleId || '',
                //             lng: locale || DEFAULT_LANGUAGE_CODE,
                //           }),
                //         )
                //       }}
                //     >
                //       <ExternalLink size={24} stroke={'#E57373'} />
                //     </IconButton>
                //   )
                // }
              />
            </Box>
          }
        />
      )}
      {curTab === AccountTabLinks.users && <AccountUsers />}
      {curTab === AccountTabLinks.billing && !hideBilling && (
        <AccountBilling subscriptionPlan={account?.subscription_plan} />
      )}
      {curTab === AccountTabLinks.settings && <AccountSettings />}
      {(curTab === AccountTabLinks.profile || !curTab) && (
        <EditAccountForm
          locale={locale}
          handleLocale={handleLocaleChange}
          account={account}
          enableReinitialize={enableReinitialize}
        />
      )}
      <FormTimestamp
        created_at={account.created?.created_at}
        created_by={account.created?.user_name ?? account.created?.user_email}
        updated_at={account.updated?.created_at}
        updated_by={account.updated?.user_name ?? account.updated?.user_email}
      />
    </>
  )
}

export { EditAccount }
